import React from "react"
import styled from "~ui/styled"
import { NavLink, useLocation } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useStores } from "~models"
import { IconFont } from "~components/El"
import { Avatar } from "~widgets/avatar"
import useNavigation from "~hooks/useNavigation"
import {
  PC_SIDEBAR_WIDTH,
  getStoredPcTab,
  isPcTabPath,
  type PcTabPath,
} from "~ui/app-shell"

const Sidebar = styled.nav`
  position: relative;
  z-index: 10001;
  width: ${PC_SIDEBAR_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 16px;
  gap: 0;
  background: var(--pc-sidebar-bg);
  border-right: 1px solid var(--pc-border);
  box-sizing: border-box;
`

const AvatarWrap = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }
`

const NavItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`

const NavItem = styled(NavLink)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 4px 0;
  text-decoration: none;
  user-select: none;
  cursor: pointer;
`

const NavLabel = styled.span<{ $active?: boolean }>`
  font-size: 11px;
  line-height: 1.2;
  color: ${({ $active }) => ($active ? "var(--pc-sidebar-active)" : "var(--pc-sidebar-text)")};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`

const IconWrap = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
`

const NavBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -10px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.danger};
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  font-weight: 600;
  box-sizing: border-box;
  white-space: nowrap;
`

const TAB_NAV: { path: PcTabPath; label: string; icon: string }[] = [
  { path: "/chats", label: "聊天", icon: "lt" },
  { path: "/contacts", label: "好友", icon: "hy" },
  { path: "/groups", label: "群聊", icon: "txl" },
  { path: "/me", label: "我的", icon: "wd" },
]

function PcSidebarBase() {
  const location = useLocation()
  const navigation = useNavigation()
  const { messageStore, friendStore, userStore } = useStores()
  const currentUser = userStore.currentUser

  const listPath: PcTabPath = isPcTabPath(location.pathname)
    ? location.pathname
    : getStoredPcTab()

  const unread = messageStore.unread
  const friendCount = friendStore.friendsCount

  const badgeFor = (path: PcTabPath) => {
    if (path === "/chats" && unread > 0) return unread > 99 ? "99+" : String(unread)
    if (path === "/contacts" && friendCount > 0) return String(friendCount)
    return null
  }

  const isActive = (path: PcTabPath, navIsActive: boolean) =>
    navIsActive || listPath === path

  return (
    <Sidebar aria-label="主导航">
      <AvatarWrap
        type="button"
        title="个人资料"
        aria-label="个人资料"
        onClick={() => navigation.push("BasicInfo")}
      >
        {currentUser ? (
          <Avatar size={44} avatar={currentUser.avatar} name={currentUser.name} />
        ) : (
          <Avatar size={44} name="?" />
        )}
      </AvatarWrap>

      <NavItems>
        {TAB_NAV.map((tab) => {
          const badge = badgeFor(tab.path)
          return (
            <NavItem key={tab.path} to={tab.path} title={tab.label}>
              {({ isActive: navIsActive }) => {
                const active = isActive(tab.path, navIsActive)
                return (
                  <>
                    <IconWrap>
                      <IconFont
                        name={tab.icon}
                        size={24}
                        color={active ? "#07c160" : "#b0b4bc"}
                      />
                      {badge ? <NavBadge>{badge}</NavBadge> : null}
                    </IconWrap>
                    <NavLabel $active={active}>{tab.label}</NavLabel>
                  </>
                )
              }}
            </NavItem>
          )
        })}
      </NavItems>
    </Sidebar>
  )
}

export default observer(PcSidebarBase)
